import React, {FC} from 'react';
import styled, {css, keyframes} from 'styled-components';

import {ThemeContextReader} from '../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface SkeletonProps {
  className?: string;
  /* Width of the skeleton */
  width?: string;
  /* Height of the skeleton */
  height?: string;
  /* Border Radius of the skeleton */
  borderRadius?: string;
  /* Duration of the animation */
  duration?: number;
  /* Scheme of the skeleton */
  scheme?: SkeletonColorSchemesEnum;
}

/*
 * Styles.
 */

const skeletonKeyFrames = keyframes`
  from {
    transform: translateX(calc(-200px));
  }
  to {
    transform: translateX(calc(200px + 100%))
  }
`;

interface SkeletonStyleProps {
  $width?: string;
  $height?: string;
  $borderRadius?: string;
  $duration?: number;
  $scheme?: SkeletonColorSchemesEnum;
}

const StyledSkeletonSpan = styled.div<SkeletonStyleProps>`
  position: relative;
  border-radius: ${(p) => p.$borderRadius || '4px'};
  display: inline-block;
  line-height: 1;
  width: ${(p) => p.$width || '100%'};
  height: ${(p) => p.$height || '100%'};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 200px;
    height: 100%;
    background-size: 200px 100%;
    background-repeat: no-repeat;
    animation: ${skeletonKeyFrames} ${(p) => p.$duration || 1.2}s ease-in-out infinite;
  }

  ${(p) => addSchemeStyles(p.$scheme)}
`;

export enum SkeletonColorSchemesEnum {
  GREY10 = 'shade10',
  GREY30 = 'shade30',
  GREY40 = 'shade40',
}

type SkeletonColorSchemes = Record<SkeletonColorSchemesEnum, [ThemeContextReader, ThemeContextReader]>;
const skeletonColorSchemes: SkeletonColorSchemes = {
  [SkeletonColorSchemesEnum.GREY10]: [(p) => p.theme.greys.shade10, (p) => p.theme.greys.shade20],
  [SkeletonColorSchemesEnum.GREY30]: [(p) => p.theme.greys.shade30, (p) => p.theme.greys.shade40],
  [SkeletonColorSchemesEnum.GREY40]: [(p) => p.theme.greys.shade40, (p) => p.theme.greys.shade50],
};

const addSchemeStyles = (scheme: SkeletonColorSchemesEnum = SkeletonColorSchemesEnum.GREY30) => {
  const [firstShade, secondShade] = skeletonColorSchemes[scheme];

  return css`
    background-color: ${firstShade};

    &::before {
      background-image: linear-gradient(90deg, ${firstShade}, ${secondShade}, ${firstShade});
    }
  `;
};

/*
 * Component.
 */

export const Skeleton: FC<SkeletonProps> = (props) => (
  <StyledSkeletonSpan
    className={props.className}
    $width={props.width}
    $height={props.height}
    $borderRadius={props.borderRadius}
    $duration={props.duration}
    $scheme={props.scheme}
  >
    &zwnj;
  </StyledSkeletonSpan>
);
